'use client';

/**
 * Inbox master list, rendered inside the secondary context panel (the
 * sub-sidebar) — the master half of the inbox master–detail layout.
 *
 * The detail half is the main column: `/inbox` shows a "nothing selected"
 * placeholder, `/inbox/[matchId]` renders the conversation. This panel owns
 * the role tabs (received/sent), the status filter chips, and the scrollable
 * inquiry list; selecting a row navigates to the detail route while the panel
 * stays put and highlights the active inquiry — mirroring chat/[sessionId].
 */

import { AlertCircle, Loader2, MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import professionalsService from '@/services/professionals-services';
import type {
  LawyerInboxResponse,
  MatchStatus,
  ProfessionalMatch,
} from '@/types/professionals-types';

const PAGE_SIZE = 25;

type InboxTab = 'received' | 'sent';
type StatusTab = 'all' | MatchStatus;

const STATUS_TABS: readonly StatusTab[] = [
  'all',
  'suggested',
  'contacted',
  'responded',
] as const;

const STATUS_TAB_LABEL_KEY: Record<StatusTab, string> = {
  all: 'tabAll',
  suggested: 'tabPending',
  contacted: 'tabContacted',
  responded: 'tabResponded',
  declined: 'tabResponded',
  expired: 'tabResponded',
};

const STATUS_LABEL_KEY: Record<MatchStatus, string> = {
  suggested: 'statusSuggested',
  contacted: 'statusContacted',
  responded: 'statusResponded',
  declined: 'statusDeclined',
  expired: 'statusExpired',
};

const STATUS_VARIANT: Record<
  MatchStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  suggested: 'default',
  contacted: 'secondary',
  responded: 'outline',
  declined: 'outline',
  expired: 'outline',
};

function formatRelativeTime(
  isoTimestamp: string,
  t: ReturnType<typeof useTranslations>
): string {
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.max(1, Math.round((Date.now() - then) / 60_000));
  if (diffMin < 60) return t('matchAge', { minutes: diffMin });
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return t('matchAgeHours', { hours: diffH });
  const diffD = Math.round(diffH / 24);
  return t('matchAgeDays', { days: diffD });
}

/**
 * The selected match id when on an inbox-detail route, else null. Reads the
 * `[matchId]` dynamic segment directly via useParams — the same reliable,
 * locale/slug-agnostic source the detail page and chat's SessionItem use
 * (pathname parsing is fragile across localized URLs).
 */
function useActiveMatchId(): string | null {
  const params = useParams();
  const matchId = params['matchId'];
  return typeof matchId === 'string' ? matchId : null;
}

interface PanelRowProps {
  match: ProfessionalMatch;
  active: boolean;
  onNavigate?: (() => void) | undefined;
}

function PanelRow({ match, active, onNavigate }: PanelRowProps) {
  const t = useTranslations('Lawyers.inbox');
  const createdAt = match.created_at ?? match.suggested_at?.toString();

  return (
    <Link
      href={{
        pathname: '/inbox/[matchId]',
        params: { matchId: String(match.id) },
      }}
      onClick={() => onNavigate?.()}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative block rounded-md px-2.5 py-2 transition-colors',
        active ? 'bg-interactive-selected' : 'hover:bg-interactive-hover'
      )}
    >
      {active && (
        <span
          aria-hidden='true'
          className='absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-tertiary'
        />
      )}
      <div className='mb-1 flex items-center gap-2'>
        <Badge
          variant={STATUS_VARIANT[match.status]}
          className='px-1.5 py-0.5 text-[10px] leading-none'
        >
          {t(STATUS_LABEL_KEY[match.status] as Parameters<typeof t>[0])}
        </Badge>
        {createdAt && (
          <span className='text-[11px] tabular-nums text-content-muted'>
            {formatRelativeTime(createdAt, t)}
          </span>
        )}
      </div>
      <p className='line-clamp-2 text-sm text-content-primary'>
        {match.question_summary || t('topicFallback')}
      </p>
      {match.client_location && (
        <div className='mt-1 flex items-center gap-1 text-xs text-content-secondary'>
          <MapPin className='h-3 w-3 shrink-0' />
          <span className='truncate'>{match.client_location}</span>
        </div>
      )}
    </Link>
  );
}

interface InboxListPanelProps {
  // Called after a row navigates — used on mobile to close the drawer.
  onNavigate?: (() => void) | undefined;
}

export function InboxListPanel({ onNavigate }: InboxListPanelProps) {
  const t = useTranslations('Lawyers.inbox');
  const { isProfessional } = useAuthUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const activeMatchId = useActiveMatchId();

  // Same initial-tab resolution as the old page (claimed pros → received,
  // everyone else → sent; ?tab= deep-links win). Mirrored in inbox/page.test.ts.
  const tabFromUrl = searchParams.get('tab');
  const initialTab: InboxTab =
    tabFromUrl === 'sent' || tabFromUrl === 'received'
      ? tabFromUrl
      : isProfessional
        ? 'received'
        : 'sent';

  const [tab, setTab] = useState<InboxTab>(initialTab);
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [items, setItems] = useState<ProfessionalMatch[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const switchTab = useCallback(
    (next: InboxTab) => {
      setTab(next);
      setStatusTab('all');
      const params = new URLSearchParams(searchParams.toString());
      params.set('tab', next);
      router.replace(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const load = useCallback(
    async (
      targetPage: number,
      activeTab: InboxTab,
      activeStatus: StatusTab
    ) => {
      setLoading(true);
      setError(false);
      try {
        if (activeTab === 'received') {
          const response = await professionalsService.getLawyerInbox({
            page: targetPage,
            page_size: PAGE_SIZE,
            ...(activeStatus !== 'all'
              ? { match_status: activeStatus as MatchStatus }
              : {}),
          });
          if (response.status === 'success' && response.data) {
            const body = response.data as LawyerInboxResponse;
            setItems((prev) =>
              targetPage === 1 ? body.matches : [...prev, ...body.matches]
            );
            setHasMore(body.has_more);
            setPage(body.page);
          } else {
            setError(true);
          }
        } else {
          const response = await professionalsService.getMyMatches();
          if (response.status === 'success' && Array.isArray(response.data)) {
            setItems(response.data);
            setHasMore(false);
            setPage(1);
          } else {
            setError(true);
          }
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(false);
    void load(1, tab, statusTab);
  }, [tab, statusTab, load]);

  const showRoleTabs = isProfessional;
  const showStatusFilter = tab === 'received';

  return (
    <div className='flex h-full min-h-0 flex-col'>
      {/* Role tabs (received / sent) — only when the user can receive. */}
      {showRoleTabs && (
        <div className='px-3 pt-1'>
          <div
            className='inline-flex w-full items-center rounded-lg border border-line-default bg-surface-subtle p-0.5'
            role='tablist'
            aria-label={t('unifiedTitle')}
            data-tour='inbox-tabs'
          >
            {(['received', 'sent'] as const).map((roleTab) => (
              <button
                key={roleTab}
                type='button'
                role='tab'
                aria-selected={tab === roleTab}
                onClick={() => switchTab(roleTab)}
                className={cn(
                  'flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  tab === roleTab
                    ? 'bg-card text-content-primary shadow-sm'
                    : 'text-content-secondary hover:text-content-primary'
                )}
              >
                {t(roleTab === 'received' ? 'tabReceived' : 'tabSent')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status filter chips — Received only. */}
      {showStatusFilter && (
        <div
          className='flex flex-wrap gap-1.5 px-3 pt-2'
          role='tablist'
          aria-label={t('tabReceived')}
          data-tour='inbox-status'
        >
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              type='button'
              role='tab'
              aria-selected={statusTab === s}
              onClick={() => setStatusTab(s)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                statusTab === s
                  ? 'border-brand-tertiary bg-brand-tertiary/10 text-brand-tertiary'
                  : 'border-line-default text-content-secondary hover:border-line-strong hover:text-content-primary'
              )}
            >
              {t(STATUS_TAB_LABEL_KEY[s] as Parameters<typeof t>[0])}
            </button>
          ))}
        </div>
      )}

      <div
        className='mt-2 min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-3'
        data-tour='inbox-list'
      >
        {loading && items.length === 0 && (
          <div className='flex items-center justify-center gap-2 py-10 text-sm text-content-secondary'>
            <Loader2 className='h-4 w-4 animate-spin' />
            {t('loading')}
          </div>
        )}

        {error && items.length === 0 && (
          <div className='mx-1 flex items-start gap-2 rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-xs text-status-error'>
            <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
            <span>{t('loadingError')}</span>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className='flex h-full items-center justify-center px-3 py-8'>
            <p className='text-center text-xs text-content-muted'>
              {tab === 'received'
                ? isProfessional
                  ? t('emptyTitle')
                  : t('emptyClaimPromptTitle')
                : t('clientEmptyTitle')}
            </p>
          </div>
        )}

        {items.map((match) => (
          <PanelRow
            key={match.id}
            match={match}
            active={String(match.id) === activeMatchId}
            onNavigate={onNavigate}
          />
        ))}

        {hasMore && tab === 'received' && (
          <div className='px-1 pt-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='w-full'
              disabled={loading}
              onClick={() => void load(page + 1, tab, statusTab)}
            >
              {loading ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  {t('loading')}
                </>
              ) : (
                t('loadMore')
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
